import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Stack,
  Typography,
  IconButton,
  Alert,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Iconchecked from '../../../asset/icons/icon_checked.svg';
import IconUnchecked from '../../../asset/icons/icon_unchecked.svg';
import {isAllOtherOptionName, OtherNamingCmpt} from './OtherNamingCmpt';
import {IEventGrouped} from '../../../react_query/event_types/List-EventByGroup';
import {useState} from 'react';

export const EventSelection = ({
  sports,
  setSports,
  otherSport,
  setOtherSport,
}: {
  sports: IEventGrouped[];
  setSports: React.Dispatch<React.SetStateAction<IEventGrouped[]>>;
  otherSport: string;
  setOtherSport: (val: string) => void;
}) => {
  const firstColumnLimit = Math.floor(
    sports && sports.length ? sports.length / 3 : 0,
  );
  const secondColumnLimit = firstColumnLimit * 2;
  const [alertMessage, setAlertMessage] = useState('');

  const handleOtherSportChange = (val: string) => {
    setOtherSport(val);
    const hasValue = val.trim().length > 0;
    setSports(prev => {
      let changed = false;
      const updated = prev.map(group => {
        if (group.sub_list.length === 0) {
          if (isAllOtherOptionName(group.name) && group.selected !== hasValue) {
            changed = true;
            return {...group, selected: hasValue};
          }
          return group;
        }
        if (
          group.sub_list.some(
            sub => isAllOtherOptionName(sub.name) && sub.selected !== hasValue,
          )
        ) {
          changed = true;
          const subList = group.sub_list.map(sub =>
            isAllOtherOptionName(sub.name) ? {...sub, selected: hasValue} : sub,
          );
          return {
            ...group,
            sub_list: subList,
            selected: subList.some(sub => sub.selected),
          };
        }
        return group;
      });
      return changed ? updated : prev;
    });
  };

  const handleAddOtherSport = () => {
    if (!otherSport.trim()) return;

    setSports(prev => {
      const updated = [...prev];
      const existingGroup = updated.find(s => s.name === 'Other Sports/Events');

      // If group exists and already has 10 or more sub_list items, don't add more
      if (existingGroup && existingGroup.sub_list.length >= 10) {
        return prev;
      }

      const exists = existingGroup?.sub_list.some(
        sub => sub.name.toLowerCase() === otherSport.toLowerCase(),
      );
      if (exists) {
        setAlertMessage('This sport already exists in Other Sports/Events.');
        return prev;
      }

      const newEntry = {
        id: Date.now() + Math.random(),
        name: otherSport,
        selected: true,
        type: 'other',
      };

      if (existingGroup) {
        const exists = existingGroup.sub_list.some(
          sub => sub.name === otherSport,
        );
        if (!exists) {
          existingGroup.sub_list.push(newEntry);
          existingGroup.selected = true;
        }
      } else {
        updated.push({
          id: Date.now(),
          name: 'Other Sports/Events',
          selected: true,
          sub_list: [newEntry],
          type: 'other',
        });
      }
      setAlertMessage('');
      return updated;
    });

    handleOtherSportChange('');
  };

  const otherSportsGroup = sports.find(s => s.name === 'Other Sports/Events');
  const otherSportsCount = otherSportsGroup
    ? otherSportsGroup.sub_list.length
    : 0;
  return (
    <Box>
      <Typography variant='h5' className='font-bold mb-4'>
        Select Sport
        {alertMessage && (
          <Alert severity='error' onClose={() => setAlertMessage('')}>
            {alertMessage}
          </Alert>
        )}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={4}>
          <Stack spacing={2}>
            {sports && sports.length > 0
              ? sports.map((sport, index) => {
                  if (index <= firstColumnLimit) {
                    return (
                      <CheckboxAccordion
                        key={sport.id}
                        sport={sport}
                        setSports={setSports}
                      />
                    );
                  }
                  return null;
                })
              : null}
          </Stack>
        </Grid>
        <Grid item xs={4}>
          <Stack spacing={2}>
            {sports && sports.length > 0
              ? sports.map((sport, index) => {
                  if (index > firstColumnLimit && index <= secondColumnLimit) {
                    return (
                      <CheckboxAccordion
                        key={sport.id}
                        sport={sport}
                        setSports={setSports}
                      />
                    );
                  }
                  return null;
                })
              : null}
          </Stack>
        </Grid>
        <Grid item xs={4}>
          <Stack spacing={2}>
            {sports && sports.length > 0
              ? sports.map((sport, index) => {
                  if (index > secondColumnLimit) {
                    return (
                      <CheckboxAccordion
                        key={sport.id}
                        sport={sport}
                        setSports={setSports}
                      />
                    );
                  }
                  return null;
                })
              : null}
          </Stack>
        </Grid>
      </Grid>
      <OtherNamingCmpt
        label='Any Other Sport'
        placeholder='Enter Sport Name'
        other={otherSport}
        setOther={handleOtherSportChange}
        onAdd={handleAddOtherSport}
        disabled={otherSportsCount >= 10}
      />
    </Box>
  );
};
const CheckboxAccordion = ({
  sport,
  setSports,
}: {
  sport: IEventGrouped;
  setSports: React.Dispatch<React.SetStateAction<IEventGrouped[]>>;
}) => {
  const handleChange = (type: string, id: number) => {
    setSports(prev => {
      const finalValue = prev.map(val => {
        if (val.id === id || val.sub_list.some(subVal => subVal.id === id)) {
          const newValue = {...val};
          if (type === 'parent') {
            if (newValue.selected) {
              newValue.selected = false;
              newValue.sub_list = newValue.sub_list.map(v => {
                const newSubVal = v;
                newSubVal.selected = false;
                return newSubVal;
              });
            } else {
              newValue.selected = true;
              newValue.sub_list = newValue.sub_list.map(v => {
                const newSubVal = {...v};
                newSubVal.selected = true;
                return newSubVal;
              });
            }
          } else {
            newValue.sub_list = newValue.sub_list.map(v => {
              const newSubVal = {...v};
              if (newSubVal.id === id) {
                newSubVal.selected = !newSubVal.selected;
              }
              return newSubVal;
            });

            if (newValue.sub_list.some(subVal => subVal.selected === true)) {
              newValue.selected = true;
            } else {
              newValue.selected = false;
            }
          }
          return newValue;
        }
        return val;
      });

      return finalValue;
    });
  };

  const handleRemoveOtherSport = (id: number) => {
    setSports(prev => {
      return prev.map(group => {
        if (group.name === 'Other Sports/Events') {
          const updatedSubList = group.sub_list.filter(sub => sub.id !== id);
          const anySelected = updatedSubList.some(sub => sub.selected);

          return {
            ...group,
            sub_list: updatedSubList,
            selected: anySelected, // ensure parent is selected if any sub item is
          };
        }
        return group;
      });
    });
  };

  if (sport.sub_list.length > 0) {
    return (
      <Accordion
        className={`bg-blue-variant53 w-full radius-5 ${
          sport.selected ? 'border-green-variant18' : ''
        }`}>
        <AccordionSummary
          sx={{
            '.MuiAccordionSummary-content': {margin: '0 !important'},
            '&.MuiButtonBase-root': {
              minHeight: '40px !important',
            },
          }}
          expandIcon={<ArrowDropDownIcon />}
          aria-controls='panel-content'
          id='panel-header'>
          <FormControlLabel
            control={
              <Checkbox
                checked={sport.selected}
                onChange={() => handleChange('parent', sport.id)}
                icon={<img src={IconUnchecked} alt='' />}
                checkedIcon={<img src={Iconchecked} alt='' />}
              />
            }
            label={sport.name}
          />
        </AccordionSummary>
        <AccordionDetails className='pl-8'>
          {sport.sub_list.map(val => (
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='space-between'
              key={val.id}
              sx={{pr: 1}}>
              <FormControlLabel
                className='m-0'
                control={
                  <Checkbox
                    checked={val.selected}
                    onChange={() => handleChange('child', val.id)}
                    icon={<img src={IconUnchecked} alt='' />}
                    checkedIcon={<img src={Iconchecked} alt='' />}
                  />
                }
                label={val.name}
              />
              {sport.name === 'Other Sports/Events' && val.type === 'other' && (
                <IconButton
                  size='small'
                  onClick={() => handleRemoveOtherSport(val.id)}>
                  ✕
                </IconButton>
              )}
            </Stack>
          ))}
        </AccordionDetails>
      </Accordion>
    );
  } else {
    return (
      <AccordionSummary
        className={`bg-blue-variant53 w-full radius-5 ${
          sport.selected ? 'border-green-variant18' : ''
        }`}
        sx={{
          width: '100%',
          '.MuiAccordionSummary-content': {margin: '0 !important'},
          '&.MuiButtonBase-root': {
            minHeight: '40px !important',
          },
        }}
        expandIcon={<ArrowDropDownIcon sx={{opacity: '0.3'}} />}
        aria-controls='panel-content'
        id='panel-header'>
        <FormControlLabel
          control={
            <Checkbox
              checked={sport.selected}
              onChange={() => handleChange('parent', sport.id)}
              icon={<img src={IconUnchecked} alt='' />}
              checkedIcon={<img src={Iconchecked} alt='' />}
            />
          }
          label={sport.name}
        />
      </AccordionSummary>
    );
  }
};
